import calculationsQueries from '@/api/modules/calculationsQueries'
import machinesQueries from '@/api/modules/machinesQueries'
import materialsQueries from '@/api/modules/materialsQueries'
import toolsQueries from '@/api/modules/toolsQueries'
import { MATERIAL_TYPES } from '@/constants'
import { useCalculationsService } from '@/views/Calculations/services/calculationsService'

export function createCalculationsActions(state) {
    const calculationsService = useCalculationsService()

    const prefetchCalculationResources = async () => {
        state.calculationResourcesLoading.value = true

        try {
            const [
                machinesResponse,
                papersResponse,
                certificatesResponse,
                templatesResponse,
                elasticMaterialsResponse,
                dieCuttersResponse,
                dieCuttersOwnershipTypesResponse
            ] = await Promise.all([
                machinesQueries.fetchCalculationMachines({ isVisible: true }),
                materialsQueries.fetchPapers(),
                materialsQueries.fetchPaperCertificates(),
                calculationsQueries.fetchCalculationColourTemplates(),
                materialsQueries.fetchMaterials({ isVisible: true, materialType_id: MATERIAL_TYPES.ELASTIC }),
                calculationsQueries.fetchCalculationDieCutters(),
                toolsQueries.fetchDieCuttersOwnershipTypes()
            ])

            state.calculationMachines.value = machinesResponse?.data?.results || machinesResponse?.data || []
            state.calculationPapers.value = papersResponse?.data?.results || papersResponse?.data || []
            state.calculationPaperCertificates.value = certificatesResponse?.data?.results || certificatesResponse?.data || []
            state.calculationColourTemplates.value = templatesResponse?.data?.results || templatesResponse?.data || []
            state.calculationElasticMaterials.value =
                elasticMaterialsResponse?.data?.results || elasticMaterialsResponse?.data || []
            state.calculationDieCutters.value = dieCuttersResponse?.data?.results || dieCuttersResponse?.data || []
            state.calculationDieCutterOwnershipTypes.value =
                dieCuttersOwnershipTypesResponse?.data?.results || dieCuttersOwnershipTypesResponse?.data || []

            return {
                machinesResponse,
                papersResponse,
                certificatesResponse,
                templatesResponse,
                elasticMaterialsResponse,
                dieCuttersResponse,
                dieCuttersOwnershipTypesResponse
            }
        } finally {
            state.calculationResourcesLoading.value = false
        }
    }

    const fetchCalculations = async (queryString = '') => {
        try {
            state.calculationsLoading.value = true
            const response = await calculationsService.fetchCalculations(queryString)
            state.calculations.value = response?.data?.results || []
            return response
        } finally {
            state.calculationsLoading.value = false
        }
    }

    const temporarySaveCalculationData = (payload) => {
        if (!payload || typeof payload !== 'object') return
        if (!state.calculation.value) return

        state.calculation.value = {
            ...state.calculation.value,
            ...payload
        }
    }

    const createCalculation = async (payload) => {
        const response = await calculationsService.createCalculation(payload)
        state.calculation.value = response?.data || null
        return response
    }

    const fetchCalculation = async (calculationId) => {
        try {
            state.calculationLoading.value = true
            const response = await calculationsService.fetchCalculation(calculationId)
            state.calculation.value = response?.data || null
            return response
        } finally {
            state.calculationLoading.value = false
        }
    }

    const fetchCalculationStatuses = async () => {
        const response = await calculationsService.fetchCalculationStatuses()
        state.calculationStatusList.value = response?.data?.results || []
        return response
    }

    const updateCalculation = async (calculation) => {
        const response = await calculationsService.updateCalculation(calculation)
        state.calculation.value = response?.data || null
        return response
    }

    const updateCalculationTraderMargin = async (payload) => {
        const response = await calculationsService.updateCalculationTraderMargin(payload)
        if (state.calculation.value?.id) {
            await fetchCalculation(state.calculation.value.id)
        }
        return response
    }

    const deleteCalculation = async (calculationId) => {
        return await calculationsService.deleteCalculation(calculationId)
    }

    const copyCalculation = async (payload) => {
        return await calculationsService.copyCalculation(payload)
    }

    const copyCalculationImpositionParameters = async (payload) => {
        return await calculationsService.copyCalculationImpositionParameters(payload)
    }

    const fetchCalculationValidation = async (calculationId) => {
        return await calculationsService.fetchCalculationValidation(calculationId)
    }

    const fetchCalculationImpositionTask = async (taskId) => {
        return await calculationsService.fetchCalculationImpositionTask(taskId)
    }

    const updateCalculationImpositionTask = async (task) => {
        return await calculationsService.updateCalculationImpositionTask(task)
    }

    const fetchCalculationImpositionTaskPriceComponents = async (calculationPk) => {
        return await calculationsService.fetchCalculationImpositionTaskPriceComponents(calculationPk)
    }

    const updateCalculationImpositionTaskPriceComponents = async (payload) => {
        return await calculationsService.updateCalculationImpositionTaskPriceComponents(payload)
    }

    const fetchCalculationImposition = async (impositionId) => {
        return await calculationsService.fetchCalculationImposition(impositionId)
    }

    const updateCalculationImposition = async (imposition) => {
        return await calculationsService.updateCalculationImposition(imposition)
    }

    const updateCalculationImpositionPaperConfiguration = async (payload) => {
        return await calculationsService.updateCalculationImpositionPaperConfiguration(payload)
    }

    const fetchCalculationPrintColours = async (machineId) => {
        return await calculationsService.fetchCalculationPrintColours(machineId)
    }

    const fetchCalculationColourTemplates = async () => {
        return await calculationsService.fetchCalculationColourTemplates()
    }

    const printCalculationPriceOffer = async (payload) => {
        return await calculationsService.printCalculationPriceOffer(payload)
    }

    const printCalculationReport = async (payload) => {
        return await calculationsService.printCalculationReport(payload)
    }

    const setCalculationsFilters = (filters) => {
        state.filters.value = { ...filters }
    }

    const clearCalculationsFilters = () => {
        state.filters.value = {
            search: null,
            status_ids: null,
            customerName: null,
            createdBy_id: null,
            createdAt__gte: null,
            createdAt__lte: null
        }
    }

    return {
        fetchCalculations,
        temporarySaveCalculationData,
        createCalculation,
        fetchCalculation,
        prefetchCalculationResources,
        fetchCalculationStatuses,
        updateCalculation,
        updateCalculationTraderMargin,
        deleteCalculation,
        copyCalculation,
        copyCalculationImpositionParameters,
        fetchCalculationValidation,
        fetchCalculationImpositionTask,
        updateCalculationImpositionTask,
        fetchCalculationImpositionTaskPriceComponents,
        updateCalculationImpositionTaskPriceComponents,
        fetchCalculationImposition,
        updateCalculationImposition,
        updateCalculationImpositionPaperConfiguration,
        fetchCalculationPrintColours,
        fetchCalculationColourTemplates,
        printCalculationPriceOffer,
        printCalculationReport,
        setCalculationsFilters,
        clearCalculationsFilters
    }
}
